import {useFormik, setNestedObjectValues} from 'formik'
import React, {useCallback, useEffect, useMemo, useState} from 'react'
import {Modal} from 'react-bootstrap'
import Container from 'react-bootstrap/Container'
import {useIntl, FormattedMessage} from 'react-intl'
import {toast} from 'react-toastify'
import * as Yup from 'yup'
import {KTSVG} from '../../../../../../_gori/helpers'
import UseYupValidate from '../../../../../../_gori/hooks/UseYupValidate'
import {useStatesGlobal} from '../../../../../../_gori/helpers/components/StatesGlobalProvider'
import {
  Button,
  InputTextFormik,
  ValidationErrorModal,
  SelectFormik,
  FormError,
} from '../../../../../../_gori/partials/widgets'
import {InputCheckboxFormik} from '../../../../../../_gori/partials/widgets/form/InputCheckboxFormik'
import AutomationRulesService from '../../../core/AutomationRulesService'
import {ConfirmActionModal} from '../../../../../../_gori/components'
import {
  AutomationRule,
  Condition,
  Action,
  ActionInputType,
  ActionType,
  BooleanOperator,
  ConditionDataType,
  ConditionField,
  OPERATORS_REQUIRING_NULL_VALUE,
  getFieldOperators,
  createEmptyCondition,
  createEmptyAction,
} from '../../../../../../_gori/constants'
import clsx from 'clsx'
import {ConditionValue} from './ConditionValue'
import {ActionValue} from './ActionValue'
import {useAutomationRulesData} from '../../../hooks'

type Props = {
  show: boolean
  handleClose: () => void
  reloadTable: () => void
  editingRule?: AutomationRule | null
}

export const AutomationRulesModal: React.FC<Props> = ({
  show,
  handleClose,
  reloadTable,
  editingRule,
}) => {
  const intl = useIntl()
  const {stringYup} = UseYupValidate()
  const {statesGlobal} = useStatesGlobal()
  const metadata = statesGlobal?.automationRulesMetadata ?? null
  const metadataLoading = !metadata
  const {
    shipFromAddresses,
    packages,
    shipFromOptions,
    tagOptions,
    presetOptions,
    packageOptions,
    integrationStores,
  } = useAutomationRulesData()

  // Shared helper: builds a ghost option for a deleted ship-from address
  // Returns null if the address is still in the live list
  const buildGhostShipFromOption = useCallback(
    (v: any) => {
      const id: number = v?.shipping_address_id
      if (id == null || shipFromOptions.some((o) => o.value === id)) return null
      const name = (
        v.from_company || `${v.from_first_name || ''} ${v.from_last_name || ''}`.trim()
      ).trim()
      const addressStr = [v.from_street1, v.from_city, v.from_state, v.from_zip]
        .filter(Boolean)
        .join(', ')
      return {
        label: [name, addressStr].filter(Boolean).join(' - '),
        value: id,
        // _addr lets AddressOptionLabel render the 2-line style even for deleted addresses
        _addr: {
          company: v.from_company || null,
          first_name: v.from_first_name || null,
          last_name: v.from_last_name || null,
          street1: v.from_street1 || '',
          city: v.from_city || '',
          state: v.from_state || '',
          zip: v.from_zip || '',
        },
        _deleted: true,
      }
    },
    [shipFromOptions]
  )

  const ghostConditionShipFrom = useMemo(() => {
    if (!editingRule) return []
    const seen = new Set<number>()
    return (editingRule.conditions_serialized?.conditions ?? [])
      .filter((c) => c.field === ConditionField.SHIP_FROM_LOCATION && c.value != null)
      .flatMap((c) => {
        const opt = buildGhostShipFromOption(c.value as any)
        if (!opt || seen.has(opt.value)) return []
        seen.add(opt.value)
        return [opt]
      })
  }, [editingRule, buildGhostShipFromOption])

  const ghostConditionPackages = useMemo(() => {
    if (!editingRule) return []
    const seen = new Set<number>()
    return (editingRule.conditions_serialized?.conditions ?? [])
      .filter((c) => c.field === ConditionField.PACKAGE_DIMENSIONS && c.value != null)
      .flatMap((c) => {
        const v = c.value as any
        const id: number = v.package_id
        if (id == null || seen.has(id) || packageOptions.some((o) => o.value === id)) return []
        seen.add(id)
        return [
          {
            label: `${v.name || ''} (${v.length} x ${v.width} x ${v.height} ${
              v.dimension_unit || ''
            })`.trim(),
            value: id,
          },
        ]
      })
  }, [editingRule, packageOptions])

  const deletedConditionStoreIndices = useMemo(() => {
    if (!editingRule) return [] as number[]
    const allStoreIds = new Set(
      Object.values(integrationStores || {}).flatMap((stores: any) => stores.map((s: any) => s.id))
    )
    return (editingRule.conditions_serialized?.conditions ?? [])
      .map((c, i) => ({c, i}))
      .filter(({c}) => {
        if (c.field !== ConditionField.STORE_NAME) return false
        if (c.value == null) return false
        const ids = Array.isArray(c.value) ? c.value : [c.value]
        return ids.some((id: any) => !allStoreIds.has(id))
      })
      .map(({i}) => i)
  }, [editingRule, integrationStores])

  const ghostActionShipFrom = useMemo(() => {
    if (!editingRule) return []
    const seen = new Set<number>()
    return (editingRule.actions_serialized?.actions ?? [])
      .filter((a) => a.type === ActionType.ASSIGN_FROM_ADDRESS)
      .flatMap((a) => {
        const opt = buildGhostShipFromOption(a.value as any)
        if (!opt || seen.has(opt.value)) return []
        seen.add(opt.value)
        return [opt]
      })
  }, [editingRule, buildGhostShipFromOption])

  const deletedActionTagIds = useMemo(() => {
    if (!editingRule) return []
    const seen = new Set<number>()
    return (editingRule.actions_serialized?.actions ?? [])
      .filter((a) => a.type === ActionType.ADD_TAG)
      .flatMap((a) => {
        const tagIds = (a.value as any)?.tag_ids
        const id: number = Array.isArray(tagIds) ? tagIds[0] : tagIds
        if (id == null || seen.has(id) || tagOptions.some((o) => o.value === id)) return []
        seen.add(id)
        return [id]
      })
  }, [editingRule, tagOptions])

  const deletedActionPresetIds = useMemo(() => {
    if (!editingRule) return []
    const seen = new Set<number>()
    return (editingRule.actions_serialized?.actions ?? [])
      .filter((a) => a.type === ActionType.APPLY_PRESET)
      .flatMap((a) => {
        const id: number = (a.value as any)?.preset_id
        if (id == null || seen.has(id) || presetOptions.some((o) => o.value === id)) return []
        seen.add(id)
        return [id]
      })
  }, [editingRule, presetOptions])

  const [validationErrors, setValidationErrors] = useState<any>()
  const [isLoadingForm, setIsLoadingForm] = useState<boolean>(false)
  const [isLoadingDelete, setIsLoadingDelete] = useState<boolean>(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

  const isEditMode = !!editingRule

  const handleCloseModal = () => {
    handleClose()
    formik.resetForm()
    setValidationErrors(undefined)
  }

  const handleDeleteConfirm = async () => {
    if (!editingRule) return
    setIsLoadingDelete(true)
    try {
      await AutomationRulesService.deleteAutomationRule(editingRule.id)
      toast.success(intl.formatMessage({id: 'DELETE_SUCCESSFULLY'}))
      handleCloseModal()
      reloadTable()
    } catch (error: any) {
      setValidationErrors(error?.response)
    } finally {
      setIsLoadingDelete(false)
      setShowDeleteConfirm(false)
    }
  }

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        name: stringYup(255, 'NAME'),
        description: Yup.string().nullable(),
        is_active: Yup.boolean(),
        useConditions: Yup.boolean(),
        conditionType: Yup.string().oneOf(['all', 'any']),
        conditions: Yup.array()
          .of(
            Yup.object().shape({
              field: Yup.string().nullable(),
              operator: Yup.string()
                .nullable()
                .test('operator-required', intl.formatMessage({id: 'REQUIRED'}), function (value) {
                  const {field} = this.parent
                  if (!field) return true
                  const fieldMeta = metadata?.conditions?.find((f) => f.field === field)
                  if (!fieldMeta || fieldMeta.data_type === ConditionDataType.BOOLEAN) return true
                  return !!value
                }),
              value: Yup.mixed()
                .nullable()
                .test('value-required', intl.formatMessage({id: 'REQUIRED'}), function (value) {
                  const {field, operator} = this.parent
                  if (!field) return true
                  const fieldMeta = metadata?.conditions?.find((f) => f.field === field)
                  if (!fieldMeta || fieldMeta.data_type === ConditionDataType.BOOLEAN) return true
                  if (!operator) return true
                  if (OPERATORS_REQUIRING_NULL_VALUE.includes(operator as any)) return true
                  return value != null
                })
                .test(
                  'condition-store-deleted',
                  intl.formatMessage({id: 'DELETED_OPTION_STORE_ERROR'}),
                  function (value) {
                    const {field} = this.parent
                    if (field !== ConditionField.STORE_NAME) return true
                    if (value == null) return true
                    const allStoreIds = new Set(
                      Object.values(integrationStores || {}).flatMap((stores: any) =>
                        stores.map((s: any) => s.id)
                      )
                    )
                    const ids = Array.isArray(value)
                      ? value.map((v: any) => (typeof v === 'object' && v !== null ? v.value : v))
                      : [typeof value === 'object' && value !== null ? (value as any).value : value]
                    const hasDeleted = ids.some((id: any) => id != null && !allStoreIds.has(id))
                    if (!hasDeleted) return true
                    return this.createError({
                      message: intl.formatMessage({id: 'DELETED_OPTION_STORE_ERROR'}),
                    })
                  }
                ),
              value_to: Yup.mixed().nullable(),
            })
          )
          .test(
            'conditions-required-when-use-conditions',
            intl.formatMessage({id: 'AT_LEAST_ONE_CONDITION_REQUIRED'}),
            function (value) {
              const {useConditions} = this.parent
              if (!useConditions) {
                return true
              }
              if (!value || value.length === 0) {
                return false
              }

              // Check if at least one condition is valid
              const hasValidCondition = value.some((condition) => {
                if (!condition.field) return false

                // Get field metadata
                const fieldMeta = metadata?.conditions?.find((f) => f.field === condition.field)
                if (!fieldMeta) return false

                // Boolean fields only need field selected
                if (fieldMeta.data_type === ConditionDataType.BOOLEAN) {
                  return true
                }

                if (!condition.operator) return false

                // Operators like is_empty / is_not_empty have no value input
                if (OPERATORS_REQUIRING_NULL_VALUE.includes(condition.operator as any)) {
                  return true
                }

                // Other fields need field, operator, and value
                return condition.value != null
              })

              return hasValidCondition ?? false
            }
          ),
        actions: Yup.array()
          .of(
            Yup.object().shape({
              type: Yup.string().nullable(),
              value: Yup.mixed()
                .nullable()
                .test(
                  'action-value-required',
                  intl.formatMessage({id: 'REQUIRED'}),
                  function (value) {
                    const {type} = this.parent
                    if (!type) return true
                    const actionMeta = metadata?.actions?.find((a) => a.type === type)
                    if (!actionMeta || actionMeta.input_type === ActionInputType.NONE) return true
                    if (value != null) return true
                    // Redirect error to the actual sub-field so SelectFormik shows the red border
                    const subFieldMap: Partial<Record<string, string>> = {
                      [ActionType.ADD_TAG]: 'tag_ids',
                      [ActionType.SET_SHIPPING_SERVICE]: 'service',
                      [ActionType.SET_WEIGHT]: 'weight',
                      [ActionType.SET_PACKAGE_DIMENSION]: 'length',
                      [ActionType.SET_CONFIRMATION_TYPE]: 'confirmation_type',
                      [ActionType.HAZMAT_SERVICE_OPTION]: 'extra_service',
                      [ActionType.INSURE_PACKAGE]: 'insurance_type',
                      [ActionType.APPLY_PRESET]: 'preset_id',
                      [ActionType.ASSIGN_FROM_ADDRESS]: 'shipping_address_id',
                    }
                    const subField =
                      subFieldMap[type] ??
                      (actionMeta.value_schema ? Object.keys(actionMeta.value_schema)[0] : null)
                    if (subField) {
                      return this.createError({
                        path: `${this.path}.${subField}`,
                        message: intl.formatMessage({id: 'REQUIRED'}),
                      })
                    }
                    return false
                  }
                )
                .test(
                  'action-weight-required',
                  intl.formatMessage({id: 'REQUIRED'}),
                  function (value) {
                    const {type} = this.parent
                    if (type !== ActionType.SET_WEIGHT) return true
                    if (value == null) return true // handled by previous test
                    if ((value as any)?.weight != null && (value as any)?.weight !== '') return true
                    return this.createError({
                      path: `${this.path}.weight`,
                      message: intl.formatMessage({id: 'REQUIRED'}),
                    })
                  }
                )
                .test(
                  'action-weight-unit-required',
                  intl.formatMessage({id: 'REQUIRED'}),
                  function (value) {
                    const {type} = this.parent
                    if (type !== ActionType.SET_WEIGHT) return true
                    if (value == null) return true // handled by first test
                    if ((value as any)?.weight_unit) return true
                    return this.createError({
                      path: `${this.path}.weight_unit`,
                      message: intl.formatMessage({id: 'REQUIRED'}),
                    })
                  }
                )
                .test(
                  'action-package-dimension-required',
                  intl.formatMessage({id: 'REQUIRED'}),
                  function (value) {
                    const {type} = this.parent
                    if (type !== ActionType.SET_PACKAGE_DIMENSION) return true
                    if (value == null) return true // handled by previous test
                    for (const field of ['length', 'width', 'height'] as const) {
                      if ((value as any)?.[field] == null || (value as any)?.[field] === '') {
                        return this.createError({
                          path: `${this.path}.${field}`,
                          message: intl.formatMessage({id: 'REQUIRED'}),
                        })
                      }
                    }
                    return true
                  }
                )
                .test(
                  'action-tag-deleted',
                  intl.formatMessage({id: 'DELETED_OPTION_TAG_ERROR'}),
                  function (value) {
                    const {type} = this.parent
                    if (type !== ActionType.ADD_TAG) return true
                    const tagId = (value as any)?.tag_ids
                    if (tagId == null) return true
                    if (tagOptions.some((o) => o.value === tagId)) return true
                    return this.createError({
                      path: `${this.path}.tag_ids`,
                      message: intl.formatMessage({id: 'DELETED_OPTION_TAG_ERROR'}),
                    })
                  }
                )
                .test(
                  'action-preset-deleted',
                  intl.formatMessage({id: 'DELETED_OPTION_PRESET_ERROR'}),
                  function (value) {
                    const {type} = this.parent
                    if (type !== ActionType.APPLY_PRESET) return true
                    const presetId = (value as any)?.preset_id
                    if (presetId == null) return true
                    if (presetOptions.some((o) => o.value === presetId)) return true
                    return this.createError({
                      path: `${this.path}.preset_id`,
                      message: intl.formatMessage({id: 'DELETED_OPTION_PRESET_ERROR'}),
                    })
                  }
                ),
            })
          )
          .test(
            'at-least-one-action-with-type',
            intl.formatMessage({id: 'AT_LEAST_ONE_ACTION_REQUIRED'}),
            function (value) {
              if (!value || value.length === 0) {
                return false
              }

              // Check if at least one action is valid
              const hasValidAction = value.some((action) => {
                if (!action.type) return false

                // Get action metadata
                const actionMeta = metadata?.actions?.find((a) => a.type === action.type)
                if (!actionMeta) return false

                // Actions with NONE input type only need type selected
                if (actionMeta.input_type === ActionInputType.NONE) {
                  return true
                }

                // Other actions need type and value
                return action.value != null
              })

              return hasValidAction ?? false
            }
          ),
      }),
    [stringYup, intl, metadata, tagOptions, presetOptions, integrationStores]
  )

  const initialValues = useMemo(
    () => {
      const rawConditions = editingRule?.conditions_serialized?.conditions ?? []
      const rawActions = editingRule?.actions_serialized?.actions ?? []

      // DB → form: convert full objects back to IDs so SelectFormik can match options
      const deserializeCondition = (condition: Partial<Condition>): Partial<Condition> => {
        if (
          condition.field === ConditionField.SHIP_FROM_LOCATION &&
          condition.value !== null &&
          typeof condition.value === 'object' &&
          !Array.isArray(condition.value)
        ) {
          return {...condition, value: (condition.value as any).shipping_address_id ?? null}
        }
        if (
          condition.field === ConditionField.PACKAGE_DIMENSIONS &&
          condition.value !== null &&
          typeof condition.value === 'object' &&
          !Array.isArray(condition.value)
        ) {
          return {...condition, value: (condition.value as any).package_id ?? null}
        }
        return condition
      }

      // DB → form: add_tag stores tag_ids as [id] array; form uses a single id (isMulti: false)
      const deserializeAction = (action: Partial<Action>): Partial<Action> => {
        if (action.type === ActionType.ADD_TAG) {
          const tagIds = (action.value as any)?.tag_ids
          return {
            ...action,
            value: {
              ...(action.value as any),
              tag_ids: Array.isArray(tagIds) ? tagIds[0] ?? null : tagIds,
            },
          }
        }
        return action
      }

      return {
        name: editingRule?.name || '',
        description: editingRule?.description || '',
        is_active: editingRule?.is_active ?? true,
        useConditions: rawConditions.length > 0,
        conditionType: (editingRule?.conditions_serialized?.aggregator as 'all' | 'any') || 'all',
        conditions: (rawConditions.length > 0
          ? rawConditions.map(deserializeCondition)
          : [createEmptyCondition()]) as Partial<Condition>[],
        actions: (rawActions.length > 0
          ? rawActions.map(deserializeAction)
          : [createEmptyAction()]) as Partial<Action>[],
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editingRule]
  )

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values) => {
      setIsLoadingForm(true)

      const serializeConditionValue = (condition: Partial<Condition>) => {
        const value = condition.value
        if (value == null) return value

        // Multi-select: [{label, value}] → extract flat value array
        if (Array.isArray(value)) {
          if (
            value.length > 0 &&
            typeof value[0] === 'object' &&
            value[0] !== null &&
            'value' in value[0]
          ) {
            return (value as any[]).map((opt) => opt.value)
          }
          return value
        }

        // SHIP_FROM_LOCATION: id → full address object
        if (condition.field === ConditionField.SHIP_FROM_LOCATION && typeof value === 'number') {
          const addr = shipFromAddresses.find((a) => a.id === value)
          if (!addr) {
            // Address deleted — recover the original full object stored in editingRule
            const original = editingRule?.conditions_serialized?.conditions?.find(
              (c) =>
                c.field === ConditionField.SHIP_FROM_LOCATION &&
                typeof c.value === 'object' &&
                c.value !== null &&
                (c.value as any).shipping_address_id === value
            )
            return original?.value ?? value
          }
          return {
            shipping_address_id: addr.id,
            from_company: addr.company || null,
            from_first_name: addr.first_name || null,
            from_last_name: addr.last_name || null,
            from_street1: addr.street1,
            from_street2: addr.street2 || null,
            from_city: addr.city,
            from_state: addr.state,
            from_zip: addr.zip,
            from_country: addr.country || null,
            from_phone: addr.phone || null,
            from_email: addr.email || null,
          }
        }

        // PACKAGE_DIMENSIONS: id → package object
        if (condition.field === ConditionField.PACKAGE_DIMENSIONS && typeof value === 'number') {
          const pkg = packages.find((p) => p.id === value)
          if (!pkg) {
            // Package deleted — recover the original full object stored in editingRule
            const original = editingRule?.conditions_serialized?.conditions?.find(
              (c) =>
                c.field === ConditionField.PACKAGE_DIMENSIONS &&
                typeof c.value === 'object' &&
                c.value !== null &&
                (c.value as any).package_id === value
            )
            return original?.value ?? value
          }
          return {
            package_id: pkg.id,
            name: pkg.name || null,
            length: pkg.length,
            width: pkg.width,
            height: pkg.height,
            dimension_unit: pkg.unit || 'in',
          }
        }

        return value
      }

      const serializeActionValue = (action: Partial<Action>) => {
        if (action.type === ActionType.ADD_TAG) {
          const tagId = (action.value as any)?.tag_ids
          return {tag_ids: tagId != null ? [tagId] : []}
        }
        if (action.type === ActionType.ASSIGN_FROM_ADDRESS) {
          const id = (action.value as any)?.shipping_address_id
          const addr = shipFromAddresses.find((a) => a.id === id)
          if (!addr) return action.value
          return {
            shipping_address_id: addr.id,
            from_company: addr.company || null,
            from_first_name: addr.first_name || null,
            from_last_name: addr.last_name || null,
            from_street1: addr.street1,
            from_street2: addr.street2 || null,
            from_city: addr.city,
            from_state: addr.state,
            from_zip: addr.zip,
            from_country: addr.country,
            from_phone: addr.phone || null,
            from_email: addr.email || null,
          }
        }
        return action.value
      }

      try {
        const payload = {
          name: values.name,
          description: values.description || null,
          is_active: values.is_active,
          conditions_serialized: {
            aggregator: values.conditionType,
            conditions: values.useConditions
              ? values.conditions
                  .filter((c) => !!c.field)
                  .map((c) => ({...c, value: serializeConditionValue(c)}))
              : [],
          },
          actions_serialized: {
            actions: values.actions
              .filter((a) => !!a.type)
              .map((a) => ({...a, value: serializeActionValue(a)})),
          },
        }

        if (isEditMode && editingRule) {
          await AutomationRulesService.updateAutomationRule(editingRule.id, payload)
          toast.success(intl.formatMessage({id: 'UPDATED_SUCCESSFULLY'}))
        } else {
          await AutomationRulesService.createAutomationRule(payload)
          toast.success(intl.formatMessage({id: 'CREATED_SUCCESSFULLY'}))
        }

        handleCloseModal()
        reloadTable()
      } catch (error: any) {
        console.error('Error saving automation rule:', error)
        setValidationErrors(error?.response)
      } finally {
        setIsLoadingForm(false)
      }
    },
  })

  // Condition management
  const addCondition = () => {
    formik.setFieldValue('conditions', [...formik.values.conditions, createEmptyCondition()])
  }

  const removeCondition = (index: number) => {
    if (formik.values.conditions.length > 1) {
      formik.setFieldValue(
        'conditions',
        formik.values.conditions.filter((_, i) => i !== index)
      )
    }
  }

  const updateCondition = (index: number, updates: Partial<Condition>) => {
    const newConditions = [...formik.values.conditions]
    newConditions[index] = {...newConditions[index], ...updates}
    formik.setFieldValue('conditions', newConditions)
  }

  // Action management
  const addAction = () => {
    formik.setFieldValue('actions', [...formik.values.actions, createEmptyAction()])
  }

  const removeAction = (index: number) => {
    if (formik.values.actions.length > 1) {
      formik.setFieldValue(
        'actions',
        formik.values.actions.filter((_, i) => i !== index)
      )
    }
  }

  const updateAction = (index: number, updates: Partial<Action>) => {
    const newActions = [...formik.values.actions]
    newActions[index] = {...newActions[index], ...updates}
    formik.setFieldValue('actions', newActions)
  }

  // Pre-computed selected sets to avoid O(N²) recomputation on each render
  const selectedConditionFields = useMemo(
    () => formik.values.conditions.map((c) => c.field).filter(Boolean) as string[],
    [formik.values.conditions]
  )

  const selectedActionTypes = useMemo(
    () => formik.values.actions.map((a) => a.type).filter(Boolean) as string[],
    [formik.values.actions]
  )

  // Get available fields for condition dropdown (exclude already selected)
  const getAvailableFields = useCallback(
    (currentIndex: number) => {
      if (!metadata?.conditions) return []
      const currentField = formik.values.conditions[currentIndex]?.field
      return metadata.conditions
        .filter((f) => !selectedConditionFields.includes(f.field) || f.field === currentField)
        .map((f) => ({
          label: `AUTOMATION_RULE_${f.field.toUpperCase()}`,
          value: f.field,
        }))
    },
    [metadata, selectedConditionFields, formik.values.conditions]
  )

  // Get available action types (exclude already selected)
  const getAvailableActionTypes = useCallback(
    (currentIndex: number) => {
      if (!metadata?.actions) return []
      const currentType = formik.values.actions[currentIndex]?.type
      return metadata.actions
        .filter((a) => !selectedActionTypes.includes(a.type) || a.type === currentType)
        .map((a) => ({
          label: `AUTOMATION_RULE_${a.type.toUpperCase()}`,
          value: a.type,
        }))
    },
    [metadata, selectedActionTypes, formik.values.actions]
  )

  // Get operators for selected field
  const getOperatorsForField = useCallback(
    (field: any) => {
      if (!field) return []
      return getFieldOperators(field).map((op) => ({
        label: intl.formatMessage({id: op.toUpperCase()}),
        value: op,
      }))
    },
    [intl]
  )

  // Get field metadata
  const getFieldMetadata = useCallback(
    (field: any) => metadata?.conditions?.find((f) => f.field === field),
    [metadata]
  )

  // Get action metadata
  const getActionMetadata = useCallback(
    (actionType: any) => metadata?.actions?.find((a) => a.type === actionType),
    [metadata]
  )

  const handleFormSubmit = useCallback(async () => {
    const errors = await formik.validateForm()
    if (Object.keys(errors).length > 0) {
      formik.setTouched(setNestedObjectValues(errors, true))
    }
    formik.handleSubmit()
  }, [formik])

  // Show errors immediately for deleted tags/presets when modal opens in edit mode.
  // setTimeout(0) defers until AFTER formik's enableReinitialize effect has reset the form,
  // so our setFieldTouched calls aren't overwritten by the RESET_FORM dispatch.
  // initialValues (from closure) is used instead of formik.values to avoid stale closure.
  useEffect(() => {
    if (!show || !isEditMode) return
    const deletedTagIds = new Set(deletedActionTagIds)
    const deletedPresetIds = new Set(deletedActionPresetIds)
    if (
      deletedTagIds.size === 0 &&
      deletedPresetIds.size === 0 &&
      deletedConditionStoreIndices.length === 0
    )
      return

    const timer = setTimeout(() => {
      initialValues.actions.forEach((action: any, i: number) => {
        if (action.type === ActionType.ADD_TAG) {
          const tagId = action.value?.tag_ids
          if (tagId != null && deletedTagIds.has(tagId)) {
            formik.setFieldTouched(`actions[${i}].value.tag_ids`, true, false)
          }
        }
        if (action.type === ActionType.APPLY_PRESET) {
          const presetId = action.value?.preset_id
          if (presetId != null && deletedPresetIds.has(presetId)) {
            formik.setFieldTouched(`actions[${i}].value.preset_id`, true, false)
          }
        }
      })
      deletedConditionStoreIndices.forEach((i) => {
        formik.setFieldTouched(`conditions[${i}].value`, true, false)
      })
      formik.validateForm()
    }, 0)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    show,
    isEditMode,
    deletedActionTagIds,
    deletedActionPresetIds,
    initialValues.actions,
    deletedConditionStoreIndices,
  ])

  if (metadataLoading) {
    return null
  }

  return (
    <>
      {validationErrors && (
        <ValidationErrorModal
          handleClose={() => setValidationErrors(undefined)}
          response={validationErrors}
        />
      )}

      {showDeleteConfirm && (
        <ConfirmActionModal
          show={showDeleteConfirm}
          title={intl.formatMessage({id: 'ARE_YOU_SURE'})}
          message={
            <span className='fw-bold text-center'>
              <p>{intl.formatMessage({id: 'ARE_YOU_SURE_YOU_WANT_TO_PROCEED'})}</p>
              <p>
                {intl.formatMessage({
                  id: 'THIS_ACTION_CANNOT_BE_UNDONE_PLEASE_REVIEW_YOUR_DECISION_CAREFULLY_BEFORE_PROCEEDING',
                })}
              </p>
            </span>
          }
          messagePrimary={intl.formatMessage({id: 'SUBMIT'})}
          messageSecondary={intl.formatMessage({id: 'CANCEL'})}
          handlePrimaryAction={handleDeleteConfirm}
          handleSecondaryAction={() => setShowDeleteConfirm(false)}
          handleClose={() => setShowDeleteConfirm(false)}
          loadingPrimary={isLoadingDelete}
        />
      )}
      <Modal
        id='gori_modal_automation_rule'
        tabIndex={-1}
        aria-hidden='true'
        centered
        dialogClassName='mw-900px'
        show={show}
        backdrop='static'
        onHide={handleCloseModal}
      >
        <div className='modal-content'>
          <Modal.Header closeButton>
            <Modal.Title className='fw-bolder fs-1'>
              {intl.formatMessage({id: isEditMode ? 'EDIT_RULE' : 'DEFINE_RULE'})}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className='scroll-y mh-600px'>
            <Container>
              {/* Basic Information */}
              <div className='row mb-6'>
                <div className='col-12 col-md-6'>
                  <InputTextFormik
                    label={intl.formatMessage({id: 'RULE_NAME'})}
                    formik={formik}
                    name='name'
                    required
                    maxlength={50}
                    showCounterCharacter={true}
                  />
                </div>
                <div className='col-12 col-md-6 d-flex align-items-center justify-content-end'>
                  <label className='form-label me-3 fw-bold'>
                    {intl.formatMessage({id: 'ACTIVE_STATUS'})}
                  </label>
                  <div className='form-check form-switch form-check-custom form-check-solid'>
                    <input
                      className='form-check-input'
                      type='checkbox'
                      checked={formik.values.is_active}
                      onChange={(e) => formik.setFieldValue('is_active', e.target.checked)}
                    />
                  </div>
                </div>
              </div>

              <div className='row mb-8'>
                <div className='col-12'>
                  <InputTextFormik
                    label={`${intl.formatMessage({id: 'DESCRIPTION'})} (${intl.formatMessage({
                      id: 'OPTIONAL',
                    })})`}
                    formik={formik}
                    name='description'
                    required={false}
                    isTextArea
                    maxlength={150}
                    showCounterCharacter={true}
                  />
                </div>
              </div>

              {/* Conditions Section */}
              <div className='mb-8'>
                <div className='col-form-label fs-5 me-4'>
                  {intl.formatMessage({id: 'IF_AN_ORDER_MEETS_THE_FOLLOWING'})}
                </div>
                <div className='mt-2 mb-4'>
                  <div>
                    <InputCheckboxFormik
                      formik={formik}
                      type='radio'
                      label={intl.formatMessage({id: 'EVERY_ORDER'})}
                      className={'pe-4'}
                      checked={!formik.values.useConditions}
                      onClick={() => formik.setFieldValue('useConditions', false)}
                    />
                  </div>
                  <div className='mt-4'>
                    <InputCheckboxFormik
                      formik={formik}
                      type='radio'
                      label={
                        <span>
                          <FormattedMessage
                            id='ORDERS_WITH_THE_FOLLOWING_SPECIFICATIONS'
                            values={{bold: (chunks) => <strong>{chunks}</strong>}}
                          />
                        </span>
                      }
                      checked={formik.values.useConditions}
                      onClick={() => formik.setFieldValue('useConditions', true)}
                    />
                  </div>
                </div>

                {formik.values.useConditions && (
                  <div className='d-flex flex-column automation-rule-form-row p-3 rounded-3'>
                    <>
                      {/* Condition Rows */}
                      {formik.values.conditions.map((condition, index) => (
                        <div key={index}>
                          {index > 0 && (
                            <div className='d-flex align-items-center px-5'>
                              <hr className='flex-grow-1 border-gray-300' />
                              <span className='mx-3 px-5 py-1 rounded-pill bg-secondary border border-gray-300 text-dark fw-bold fs-8 text-uppercase'>
                                {intl.formatMessage({id: 'AND'})}
                              </span>
                              <hr className='flex-grow-1 border-gray-300' />
                            </div>
                          )}
                          <div className='card-body'>
                            <div className='d-flex align-items-start gap-3'>
                              <div className='flex-grow-1'>
                                <div className='row g-3'>
                                  {/* Field */}
                                  <div className='col-12 col-md-12 col-lg-4'>
                                    <SelectFormik
                                      emptyDefault={false}
                                      formik={formik}
                                      name={`conditions[${index}].field`}
                                      options={getAvailableFields(index)}
                                      hasUseIntl={true}
                                      onChange={(selected: any) => {
                                        const selectedFieldMeta = getFieldMetadata(selected?.value)
                                        updateCondition(index, {
                                          field: selected?.value || undefined,
                                          operator:
                                            selectedFieldMeta?.data_type ===
                                            ConditionDataType.BOOLEAN
                                              ? BooleanOperator.EQUALS
                                              : undefined,
                                          value:
                                            selectedFieldMeta?.data_type ===
                                            ConditionDataType.BOOLEAN
                                              ? true
                                              : null,
                                          value_to: null,
                                        })
                                      }}
                                      placeholder={intl.formatMessage({id: 'SELECT'})}
                                    />
                                  </div>

                                  {/* Operator */}
                                  {getFieldMetadata(formik.values.conditions[index].field)
                                    ?.data_type !== ConditionDataType.BOOLEAN && (
                                    <div className='col-12 col-md-6 col-lg-4'>
                                      <SelectFormik
                                        emptyDefault={false}
                                        formik={formik}
                                        name={`conditions[${index}].operator`}
                                        options={getOperatorsForField(
                                          formik.values.conditions[index].field
                                        )}
                                        onChange={(selected: any) => {
                                          updateCondition(index, {
                                            operator: selected?.value || undefined,
                                            value: null,
                                            value_to: null,
                                          })
                                        }}
                                        placeholder={intl.formatMessage({id: 'SELECT'})}
                                        disabled={!formik.values.conditions[index].field}
                                      />
                                    </div>
                                  )}
                                  {getFieldMetadata(formik.values.conditions[index].field)
                                    ?.data_type === ConditionDataType.BOOLEAN && (
                                    <div className='col-12 col-md-6 col-lg-4' />
                                  )}

                                  {/* Value */}
                                  <div className='col-12 col-md-6 col-lg-4'>
                                    <ConditionValue
                                      condition={formik.values.conditions[index]}
                                      index={index}
                                      formik={formik}
                                      fieldMeta={getFieldMetadata(
                                        formik.values.conditions[index].field
                                      )}
                                      ghostShipFromOptions={ghostConditionShipFrom}
                                      ghostPackageOptions={ghostConditionPackages}
                                    />
                                  </div>
                                </div>
                              </div>

                              {/* Remove button */}
                              <div
                                className={clsx('d-flex justify-content-center', {
                                  'opacity-50 cursor-not-allowed':
                                    formik.values.conditions.length === 1,
                                  ' text-hover-danger cursor-pointer':
                                    formik.values.conditions.length !== 1,
                                })}
                                onClick={() => removeCondition(index)}
                              >
                                <KTSVG path='/media/gori/orders/empty.svg' small={false} />
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}

                      {metadata?.conditions &&
                        formik.values.conditions.length < metadata.conditions.length && (
                          <button
                            type='button'
                            className='d-flex align-items-center btn btn-advanced-tertiary fs-inherit py-0 px-1 fw-bolder cursor-pointer'
                            onClick={addCondition}
                          >
                            <span className='fs-2 me-1'>+</span>
                            {intl.formatMessage({id: 'ADD_SPECIFICATION'})}
                          </button>
                        )}
                    </>
                  </div>
                )}
                {formik.submitCount > 0 && typeof formik.errors.conditions === 'string' && (
                  <FormError className='mt-2' message={formik.errors.conditions} />
                )}
              </div>

              {/* Actions Section */}
              <div className='mb-6'>
                <div className='fs-5 fw-bold mb-4'>
                  {intl.formatMessage({id: 'THEN_APPLY_THE_FOLLOWING_ACTIONS'})}
                </div>

                {formik.values.actions.map((action, index) => (
                  <div key={index} className='mb-4 p-3 rounded-3 automation-rule-form-row'>
                    <div className='card-body p-4'>
                      <div className='d-flex align-items-start gap-3'>
                        <div className='flex-grow-1'>
                          <div className='row g-3'>
                            {/* Action Type */}
                            <div className='col-12 col-md-6'>
                              <SelectFormik
                                emptyDefault={false}
                                formik={formik}
                                label={intl.formatMessage({id: 'ACTION'})}
                                options={getAvailableActionTypes(index)}
                                name={`actions[${index}].type`}
                                hasUseIntl={true}
                                onChange={(selected: any) => {
                                  const newType = selected?.value || undefined
                                  const defaultValue =
                                    newType === ActionType.SET_PACKAGE_DIMENSION
                                      ? {
                                          length: null,
                                          width: null,
                                          height: null,
                                          dimension_unit: 'in',
                                        }
                                      : null
                                  updateAction(index, {type: newType, value: defaultValue as any})
                                }}
                                placeholder={intl.formatMessage({id: 'SELECT'})}
                              />
                              {formik.values.actions[index].type === ActionType.INSURE_PACKAGE && (
                                <FormError
                                  className='mt-3 bg-light-danger p-3 rounded-2'
                                  textClassName='text-gray-800'
                                  message={intl.formatMessage({id: 'INSURE_PACKAGE_NOTE'})}
                                />
                              )}
                            </div>

                            {/* Action Value */}
                            <div className='col-12 col-md-6'>
                              {(() => {
                                const actionType = formik.values.actions[index].type
                                const meta = getActionMetadata(actionType)
                                const showLabel =
                                  !!actionType &&
                                  meta?.input_type !== ActionInputType.NONE &&
                                  actionType !== ActionType.SET_WEIGHT &&
                                  actionType !== ActionType.SET_PACKAGE_DIMENSION
                                if (actionType === ActionType.SET_PACKAGE_DIMENSION) {
                                  return (
                                    <div className='form-label'>
                                      {intl.formatMessage({id: 'DIMENSIONS_INCHES'})}
                                    </div>
                                  )
                                }
                                if (showLabel) {
                                  return (
                                    <div className='form-label'>
                                      {intl.formatMessage({
                                        id: `AUTOMATION_RULE_${actionType!.toUpperCase()}`,
                                      })}
                                    </div>
                                  )
                                }
                                if (!actionType) {
                                  return <div className='form-label invisible'>&nbsp;</div>
                                }
                                return null
                              })()}
                              {formik.values.actions[index].type && (
                                <ActionValue
                                  action={formik.values.actions[index]}
                                  index={index}
                                  formik={formik}
                                  actionMeta={getActionMetadata(formik.values.actions[index].type)}
                                  ghostShipFromOptions={ghostActionShipFrom}
                                />
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Remove button */}
                        <div
                          className={clsx('d-flex justify-content-center', {
                            'opacity-50 cursor-not-allowed': formik.values.actions.length === 1,
                            ' text-hover-danger cursor-pointer': formik.values.actions.length !== 1,
                          })}
                          onClick={() => removeAction(index)}
                        >
                          <KTSVG path='/media/gori/orders/empty.svg' small={false} />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

                {metadata?.actions && formik.values.actions.length < metadata.actions.length && (
                  <button
                    type='button'
                    className='d-flex align-items-center btn btn-advanced-tertiary fs-inherit py-0 px-1 fw-bolder cursor-pointer'
                    onClick={addAction}
                  >
                    <span className='fs-2 me-1'>+</span>
                    {intl.formatMessage({id: 'ADD_AN_ACTION'})}
                  </button>
                )}
                {formik.submitCount > 0 && typeof formik.errors.actions === 'string' && (
                  <FormError className='mt-2' message={formik.errors.actions} />
                )}
              </div>
            </Container>
          </Modal.Body>
          <Modal.Footer className='d-flex justify-content-between'>
            <div>
              {isEditMode && (
                <Button
                  className='btn text-danger border-danger'
                  label={intl.formatMessage({id: 'DELETE'})}
                  loadingText={intl.formatMessage({id: 'DELETE'})}
                  loading={isLoadingDelete}
                  event={() => setShowDeleteConfirm(true)}
                />
              )}
            </div>
            <div className='d-flex gap-2'>
              <Button
                className='btn btn-advanced-secondary'
                label={intl.formatMessage({id: 'CANCEL'})}
                event={handleCloseModal}
              />
              <Button
                className='btn btn-advanced-primary'
                loading={isLoadingForm}
                loadingText={intl.formatMessage({id: 'SAVING'})}
                event={handleFormSubmit}
                label={intl.formatMessage({id: 'SAVE'})}
              />
            </div>
          </Modal.Footer>
        </div>
      </Modal>
    </>
  )
}
